import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSingleAstroTicket } from "../../Redux/Reducers/AstroTicketReducer";
import socket from "../../socket/socket";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function AstrologerSupportList() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [messagesApiData, setMessagesApiData] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [connected, setConnected] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPendingLoading, setIsPendingLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);
  const [loadedImages, setLoadedImages] = useState({}); 

  const ticketData = useSelector(
    (state) => state?.AstroTicketReducer?.singleTicket || {}
  );

  const senderData = JSON.parse(localStorage.getItem("vedhvidhi-admin-data"));
  const senderId = String(senderData?._id);
  const messagesEndRef = useRef(null);

  const getSenderId = (msg) => {
    if (!msg?.sender) return null;
    return typeof msg.sender === "object" ? msg.sender._id : msg.sender;
  };

  const getReceiverId = (msg) => {
    if (!msg?.receiver) return null;
    return typeof msg.receiver === "object" ? msg.receiver._id : msg.receiver;
  };

  const isImage = (url) => {
    if (!url) return false;
    const cleanUrl = url.split("?")[0];
    return /\.(jpeg|jpg|gif|png|webp|svg|heic)$/i.test(cleanUrl);
  };

  useEffect(() => {
    if (id) dispatch(fetchSingleAstroTicket({ id, userType: "astro" }));
  }, [dispatch, id]);

  useEffect(() => {
    socket.connect();
    socket.on("connect", () => {
      setConnected(true);
      if (senderId) socket.emit("new user", senderId);
      setIsInitialLoading(false);
    });
    socket.on("disconnect", () => setConnected(false));
    return () => {
      socket.off("connect");
      socket.off("disconnect");
      socket.disconnect();
    };
  }, [senderId]);

  useEffect(() => {
    if (!ticketData?.ticketId || !senderId) return;
    setIsPendingLoading(true);

    const privateMessageHandler = (message) => {
      if (message?.ticketId !== ticketData.ticketId) return;
      const sender = getSenderId(message);
      const receiver = getReceiverId(message);
      if (sender !== senderId && receiver !== senderId) return;

      const newMessageId = message?._id || message?.timeStamp;
      setMessages((prev) => {
        const isDuplicate = prev.some(
          (msg) => (msg?._id || msg?.timeStamp) === newMessageId
        );
        if (isDuplicate) return prev;
        return [...prev, message];
      });
    };

    const pendingMessageHandler = (pendingMessages) => {
      const filtered = pendingMessages.filter((msg) => {
        return (
          msg?.ticketId === ticketData.ticketId &&
          (getSenderId(msg) === senderId || getReceiverId(msg) === senderId)
        );
      });
      setMessages((prev) => [...prev, ...filtered]);
      setIsPendingLoading(false);
    };

    socket.on("private message", privateMessageHandler);
    socket.on("pending messages", pendingMessageHandler);

    return () => {
      socket.off("private message", privateMessageHandler);
      socket.off("pending messages", pendingMessageHandler);
    };
  }, [ticketData?.ticketId, senderId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, messagesApiData]);

  useEffect(() => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    const fetchData = async () => {
      if (!ticketData?.ticketId) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/chat/${ticketData.ticketId}`,
          {
            headers: { Authorization: `${token}` },
          }
        );
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();
        if (data?.data) setMessagesApiData(data.data);
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    fetchData();
  }, [ticketData?.ticketId]);

  const parseMessage = (text) => {
    const parts = text?.split(",");
    const messageObject = {};
    parts?.forEach((part) => {
      const [key, value] = part?.split(":")?.map((item) => item?.trim());
      if (key && value) messageObject[key] = value;
    });
    return messageObject;
  };

  const sendMessage = () => {
    if (
      !inputMessage.trim() ||
      !senderId ||
      !ticketData?.astrologerId ||
      !ticketData?.ticketId
    ) {
      return;
    }

    const messageData = {
      sender: senderId,
      receiver: ticketData.astrologerId,
      message: inputMessage,
      ticketId: ticketData.ticketId,
      timeStamp: new Date().toISOString(),
    };

    socket.emit("private message", messageData, (response) => {
      if (response?.status !== "ok") {
        toast.error("Message failed to send.");
      }
    });

    setInputMessage("");
  };

  const closeTicket = async () => {
    const token = localStorage.getItem("vedhvidhi-admin-token");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}/admin/ticket/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: "close" }),
        }
      );

      if (!response.ok) throw new Error(`Error: ${response.status}`);
      dispatch(fetchSingleAstroTicket({ id, userType: "astro" }));
      toast.success("Ticket closed successfully!");
      navigate("/astrologer-support");
    } catch (error) {
      toast.error("Failed to close the ticket.");
    }
  };

  return (
    <>
      <ToastContainer />
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            cursor: "zoom-out",
          }}
        >
          <img
            src={previewImage}
            alt="preview"
            style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: 10 }}
          />
        </div>
      )}

      <div className="body d-flex py-1">
        <div className="container-xxl">
          <button
            onClick={() => window.history.back()}
            style={{
              marginBottom: "10px",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: "18px",
              color: "#007bff",
              display: "flex",
              alignItems: "center",
              padding: "10px 20px",
            }}
          >
            <span style={{ marginRight: "8px" }}>&lt;</span>
            Back
          </button>

          <div className="chatSupport">
            <div className="ticket">
              <h4>Ticket: {ticketData.ticketId}</h4>
              <button onClick={closeTicket} className="closeButton">
                Close Ticket
              </button>
            </div>

            <div
              className="ticketBody"
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                padding: "10px",
                border: "1px solid #eee",
                borderRadius: "8px",
                marginBottom: "10px",
              }}
            >
              {messagesApiData.map((msg, index) => {
                const isSender = String(msg?.senderId) === senderId;
                if (index === 0 && !isSender) {
                  const parsed = parseMessage(msg.text);
                  return (
                    <div key={`api-${index}`} className="userChatLeft">
                      <div className="firstMessageUser">
                        <span><b>Name:</b> {parsed["Name"] || "N/A"}</span>
                        <span><b>Email:</b> {parsed["Email"] || "N/A"}</span>
                        <span><b>Mobile:</b> {parsed["Mobile"] || "N/A"}</span>
                        <span><b>Subject:</b> {msg.text?.match(/Subject:\s*(.*)/)?.[1] || "N/A"}</span>
                        <span><b>Description:</b> {parsed["Description"] || "N/A"}</span>
                        <span><b>Ticket ID:</b> {parsed["Ticket ID"] || "N/A"}</span>
                      </div>
                    </div>
                  );
                }

                const imageUrl = msg.attachment && isImage(msg.attachment)
                  ? msg.attachment
                  : isImage(msg.text)
                  ? msg.text
                  : null;

                return (
                  <div key={`api-${index}`} className={isSender ? "ChatRightOuter" : "ChatLeftOuter"}>
                    <p className={isSender ? "userChatRight" : "userChatLeft"}>
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt="img"
                          style={{ maxWidth: 200, cursor: "pointer" }}
                          onClick={() => setPreviewImage(imageUrl)}
                        />
                      ) : (
                        msg.text
                      )}
                      <small className="messageTime">
                        {new Date(msg.timestamp).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </p>
                  </div>
                );
              })}

              {messages.map((msg, index) => {
                const isSender = getSenderId(msg) === senderId;
                const msgKey = msg?._id || msg?.timeStamp || index;
                const imageUrl = msg?.attachment && isImage(msg.attachment)
                  ? msg.attachment
                  : isImage(msg.message)
                  ? msg.message
                  : null;

                return (
                  <div key={`live-${index}`} className={isSender ? "ChatRightOuter" : "ChatLeftOuter"}>
                    <p className={isSender ? "userChatRight" : "userChatLeft"}>
                      {imageUrl ? (
                        <>
                          {!loadedImages[msgKey] && (
                            <div style={{ width: 200, height: 150, backgroundColor: "#eee", display: "flex", justifyContent: "center", alignItems: "center", marginBottom: 5 }}>
                              <span>Loading...</span>
                            </div>
                          )}
                          <img
                            src={imageUrl}
                            alt="img"
                            style={{ maxWidth: 200, display: loadedImages[msgKey] ? "block" : "none", cursor: "pointer" }}
                            onLoad={() => setLoadedImages(prev => ({ ...prev, [msgKey]: true }))}
                            onError={(e) => {
                              setLoadedImages(prev => ({ ...prev, [msgKey]: true }));
                              e.target.style.display = "none";
                            }}
                            onClick={() => setPreviewImage(imageUrl)}
                          />
                        </>
                      ) : (
                        msg.message
                      )}
                      <small className="messageTime">
                        {new Date(msg.timeStamp).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </p>
                  </div>
                );
              })}

              <div ref={messagesEndRef} />
            </div>

            <div className="chatButtomfix" style={{ display: "flex", gap: "10px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Type a message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendMessage();
                }}
              />
              <button
                  onClick={sendMessage}
                  className="sendButton"
                  disabled={!connected || !inputMessage.trim()}
                >
                     Enter to send 
                </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AstrologerSupportList;





